/**
 * Phase 8B — MIS detail screen.
 *
 * Layout:
 *   Header — MIS name / description / cadence / FY / actuals-source
 *   Section list — one row per section, prefixed by a/b/c…
 *     Revenue / Expense: expandable sub-category list (with CC label for expense)
 *     Formula: rendered expression as pretty letters
 *   Add-section button at the bottom
 *
 * Data flow:
 *   - Fetch MIS + sections + (for each non-formula section) sub-categories
 *   - Everything is on Postgres via /api/v3/mis/*
 */

import { mdiChevronDown, mdiChevronRight, mdiPencil, mdiPlus, mdiTable, mdiTrashCan } from "@mdi/js";
import Icon from "@mdi/react";
import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useAuth } from "../../context_providers/auth/authProvider";
import LoadingWidget from "../../context_providers/modal/loader";
import {
  ToastType,
  useToast,
} from "../../context_providers/toast/toast_ctx";
import {
  fetchDelete,
  fetchGet,
  fetchPost,
  fetchPut,
} from "../../service/api_client";
import { ACTION, MODULE } from "../../utils/enums";
import { urls } from "../../utils/urls";
import SectionModal from "./components/section_modal";
import SubCategoryModal from "./components/sub_category_modal";
import {
  ACTUALS_SOURCE_LABEL,
  CADENCE_LABEL,
  Mis,
  MisSection,
  MisSectionType,
  MisSubCategory,
  orderToLetter,
  renderFormula,
  SECTION_TYPE_LABEL,
} from "./models/mis_model";

const SECTION_TYPE_PILL: Record<MisSectionType, string> = {
  revenue: "bg-emerald-100 text-emerald-800",
  expense: "bg-rose-100 text-rose-800",
  formula: "bg-sky-100 text-sky-800",
};

const MisDetailScreen: FunctionComponent = () => {
  const { id: misId } = useParams<{ id: string }>();
  const nav = useHistory();
  const { isAuthorised } = useAuth();
  const { showToast } = useToast();
  const baseUrl = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/mis`;

  const [mis, setMis] = useState<Mis | null>(null);
  const [sections, setSections] = useState<MisSection[]>([]);
  const [subCats, setSubCats] = useState<Record<string, MisSubCategory[]>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);

  // Section modal state
  const [sectionModal, setSectionModal] = useState(false);
  const [editingSection, setEditingSection] = useState<MisSection | null>(null);

  // Sub-category modal state
  const [subCatModal, setSubCatModal] = useState(false);
  const [subCatSection, setSubCatSection] = useState<MisSection | null>(null);
  const [editingSubCat, setEditingSubCat] = useState<MisSubCategory | null>(
    null,
  );

  const canManage =
    isAuthorised({ module: MODULE.mis, action: ACTION.CREATE }) ||
    isAuthorised({ module: MODULE.mis, action: ACTION.UPDATE });

  const sectionOrderById: Record<string, number> = {};
  for (const s of sections) sectionOrderById[s.id] = s.display_order;

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const misRes = await fetchGet(`${baseUrl}/${misId}`);
      if (!misRes.success) {
        showToast({ type: ToastType.error, text: misRes.error || "Failed to load MIS" });
        return;
      }
      setMis(misRes.data);

      const secRes = await fetchGet(`${baseUrl}/${misId}/section`);
      if (!secRes.success) {
        showToast({ type: ToastType.error, text: secRes.error || "Failed to load sections" });
        return;
      }
      const secs: MisSection[] = secRes.data || [];
      setSections(secs);

      // Fetch sub-cats for all non-formula sections in parallel.
      const nonFormula = secs.filter((s) => s.section_type !== "formula");
      const subCatEntries = await Promise.all(
        nonFormula.map(async (s) => {
          const r = await fetchGet(
            `${baseUrl}/${misId}/section/${s.id}/sub-category`,
          );
          return [s.id, r.success ? (r.data as MisSubCategory[]) : []] as [
            string,
            MisSubCategory[],
          ];
        }),
      );
      setSubCats(Object.fromEntries(subCatEntries));
    } catch (e: any) {
      showToast({ type: ToastType.error, text: e?.message || "Network error" });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl, misId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // -------- Section actions --------

  const openAddSection = () => {
    setEditingSection(null);
    setSectionModal(true);
  };
  const openEditSection = (s: MisSection) => {
    setEditingSection(s);
    setSectionModal(true);
  };

  const handleSectionSubmit = async (payload: {
    section_type: MisSectionType;
    title: string;
    formula_expression?: string | null;
    show_percentage: boolean;
    percentage_of_section_id?: string | null;
  }) => {
    const res = editingSection
      ? await fetchPut(`${baseUrl}/${misId}/section/${editingSection.id}`, {
          title: payload.title,
          formula_expression: payload.formula_expression,
          show_percentage: payload.show_percentage,
          percentage_of_section_id: payload.percentage_of_section_id,
        })
      : await fetchPost(`${baseUrl}/${misId}/section`, payload);
    if (!res.success) throw new Error(res.error || "Section save failed");
    showToast({
      type: ToastType.success,
      text: editingSection ? "Section updated" : "Section added",
    });
    await fetchAll();
  };

  const handleDeleteSection = async (s: MisSection) => {
    if (
      !window.confirm(
        `Delete section "${orderToLetter(s.display_order)}. ${s.title}"? This also removes its sub-categories.`,
      )
    )
      return;
    const res = await fetchDelete(`${baseUrl}/${misId}/section/${s.id}`);
    if (res.success) {
      showToast({ type: ToastType.success, text: "Section deleted" });
      fetchAll();
    } else {
      showToast({ type: ToastType.error, text: res.error || "Delete failed" });
    }
  };

  // -------- Sub-category actions --------

  const openAddSubCat = (section: MisSection) => {
    setSubCatSection(section);
    setEditingSubCat(null);
    setSubCatModal(true);
  };
  const openEditSubCat = (section: MisSection, sc: MisSubCategory) => {
    setSubCatSection(section);
    setEditingSubCat(sc);
    setSubCatModal(true);
  };

  const handleSubCatSubmit = async (payload: {
    label: string;
    cost_center_id?: string | null;
  }) => {
    if (!subCatSection) return;
    const url = `${baseUrl}/${misId}/section/${subCatSection.id}/sub-category`;
    const res = editingSubCat
      ? await fetchPut(`${url}/${editingSubCat.id}`, payload)
      : await fetchPost(url, payload);
    if (!res.success) throw new Error(res.error || "Sub-category save failed");
    showToast({
      type: ToastType.success,
      text: editingSubCat ? "Sub-category updated" : "Sub-category added",
    });
    await fetchAll();
  };

  const handleDeleteSubCat = async (
    section: MisSection,
    sc: MisSubCategory,
  ) => {
    if (!window.confirm(`Delete sub-category "${sc.label}"?`)) return;
    const res = await fetchDelete(
      `${baseUrl}/${misId}/section/${section.id}/sub-category/${sc.id}`,
    );
    if (res.success) {
      showToast({ type: ToastType.success, text: "Sub-category deleted" });
      fetchAll();
    } else {
      showToast({ type: ToastType.error, text: res.error || "Delete failed" });
    }
  };

  // -------- Render --------

  if (loading && !mis) {
    return (
      <div className="p-6">
        <LoadingWidget />
      </div>
    );
  }
  if (!mis) return null;

  return (
    <div className="p-6 max-w-5xl">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <button
            onClick={() => nav.push(urls.mis_list)}
            className="text-xs text-myPrimaryColor hover:underline mb-2"
          >
            ← All MIS
          </button>
          <h1 className="text-2xl font-bold text-gray-800">{mis.name}</h1>
          {mis.description && (
            <p className="text-sm text-gray-500 mt-1">{mis.description}</p>
          )}
          <div className="mt-3 flex flex-wrap gap-3 text-xs text-gray-600">
            <span className="border border-gray-300 rounded px-2 py-1">
              {mis.start_fy}
            </span>
            <span className="border border-gray-300 rounded px-2 py-1">
              {CADENCE_LABEL[mis.cadence_months]}
            </span>
            <span className="border border-gray-300 rounded px-2 py-1">
              {ACTUALS_SOURCE_LABEL[mis.actuals_source]}
            </span>
          </div>
        </div>
        <button
          onClick={() => nav.push(`${urls.mis_grid}/${misId}`)}
          className="bg-myPrimaryColor hover:bg-mySecondaryColor text-white rounded px-4 py-2 text-sm font-semibold flex items-center gap-1"
        >
          <Icon path={mdiTable} className="h-4 w-4" />
          Open P&amp;L Grid
        </button>
      </div>

      {/* Sections */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-700">
            Sections ({sections.length})
          </h2>
          {canManage && (
            <button
              onClick={openAddSection}
              className="bg-myPrimaryColor hover:bg-mySecondaryColor text-white rounded px-3 py-1.5 text-xs font-semibold flex items-center gap-1"
            >
              <Icon path={mdiPlus} className="h-4 w-4" />
              Add section
            </button>
          )}
        </div>

        {sections.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">
            No sections yet.{" "}
            {canManage && (
              <button
                onClick={openAddSection}
                className="text-myPrimaryColor hover:underline"
              >
                Add the first one
              </button>
            )}
            .
          </div>
        ) : (
          <div>
            {sections.map((s) => {
              const isFormula = s.section_type === "formula";
              const scs = subCats[s.id] || [];
              const isExpanded = !!expanded[s.id];
              return (
                <div key={s.id} className="border-b border-gray-100 last:border-b-0">
                  <div className="px-4 py-3 flex items-start gap-3">
                    {!isFormula ? (
                      <button
                        onClick={() =>
                          setExpanded((prev) => ({ ...prev, [s.id]: !isExpanded }))
                        }
                        className="text-gray-400 hover:text-gray-600 mt-0.5"
                      >
                        <Icon
                          path={isExpanded ? mdiChevronDown : mdiChevronRight}
                          className="h-4 w-4"
                        />
                      </button>
                    ) : (
                      <span className="w-4" />
                    )}

                    <span className="font-mono text-sm font-bold text-gray-500 w-6 mt-0.5">
                      {orderToLetter(s.display_order)}.
                    </span>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-800">
                          {s.title}
                        </span>
                        <span
                          className={`text-[10px] font-semibold rounded px-1.5 py-0.5 ${SECTION_TYPE_PILL[s.section_type]}`}
                        >
                          {SECTION_TYPE_LABEL[s.section_type]}
                        </span>
                        {s.show_percentage && (
                          <span className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-amber-100 text-amber-800">
                            + %
                          </span>
                        )}
                      </div>
                      {isFormula && s.formula_expression && (
                        <div className="text-sm text-gray-600 font-mono mt-0.5">
                          = {renderFormula(s.formula_expression, sectionOrderById)}
                        </div>
                      )}
                      {!isFormula && (
                        <div className="text-xs text-gray-500 mt-0.5">
                          {scs.length}{" "}
                          {scs.length === 1 ? "sub-category" : "sub-categories"}
                        </div>
                      )}
                    </div>

                    {canManage && (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => openEditSection(s)}
                          className="text-gray-500 hover:text-myPrimaryColor p-1"
                          title="Edit section"
                        >
                          <Icon path={mdiPencil} className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteSection(s)}
                          className="text-gray-500 hover:text-red-600 p-1"
                          title="Delete section"
                        >
                          <Icon path={mdiTrashCan} className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Sub-categories */}
                  {!isFormula && isExpanded && (
                    <div className="bg-gray-50 px-4 py-2 pl-16">
                      {scs.length === 0 ? (
                        <div className="text-xs text-gray-500 py-2">
                          No sub-categories yet.
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {scs.map((sc) => (
                            <div
                              key={sc.id}
                              className="flex items-center gap-2 py-1"
                            >
                              <span className="text-sm text-gray-700 flex-1">
                                {sc.label}
                              </span>
                              {sc.cost_center_ids.length > 0 && (
                                <span className="text-[10px] text-gray-500">
                                  {sc.cost_center_ids.length}{" "}
                                  {sc.cost_center_ids.length === 1
                                    ? "CC"
                                    : "CCs"}
                                </span>
                              )}
                              {canManage && (
                                <>
                                  <button
                                    onClick={() => openEditSubCat(s, sc)}
                                    className="text-gray-400 hover:text-myPrimaryColor p-0.5"
                                  >
                                    <Icon path={mdiPencil} className="h-3.5 w-3.5" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteSubCat(s, sc)}
                                    className="text-gray-400 hover:text-red-600 p-0.5"
                                  >
                                    <Icon
                                      path={mdiTrashCan}
                                      className="h-3.5 w-3.5"
                                    />
                                  </button>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {canManage && (
                        <button
                          onClick={() => openAddSubCat(s)}
                          className="mt-2 text-xs text-myPrimaryColor hover:underline flex items-center gap-1"
                        >
                          <Icon path={mdiPlus} className="h-3.5 w-3.5" />
                          Add sub-category
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <SectionModal
        open={sectionModal}
        initial={editingSection}
        existingSections={sections}
        onClose={() => setSectionModal(false)}
        onSubmit={handleSectionSubmit}
      />
      {subCatSection && (
        <SubCategoryModal
          open={subCatModal}
          section={subCatSection}
          initial={editingSubCat}
          onClose={() => setSubCatModal(false)}
          onSubmit={handleSubCatSubmit}
        />
      )}
    </div>
  );
};

export default MisDetailScreen;
